import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { cloneDeep, isEqual } from 'lodash';
import omitDeep from 'omit-deep';
import { IconEdit } from '../../../../../../assets/icons/icon-edit';
import {
  DialogTimePicker,
  Header,
  InputMui,
  NavHeader,
  Page,
  SubmitButton,
  Switch,
} from '../../../../../../components';
import ArrowButton from '../../../../../../components/arrow-button';
import { GateChannelAlertConfigResponse } from '../../../../../../data-access/gql-types/graphql';
import { millisecondsToTime, timeToMilliseconds } from '../../../../../../utils/helpers';
import { pathnameWithParameters } from '../../../../../../utils/location';
import { ChannelGateType, ChannelType } from '../../../../types';
import { AlertConfigRequest } from '../../../../types/openapi/openapi-types';
import './index.scss';

type AlertConfigProps = {
  channel: ChannelType;
  handleSave: (values: AlertConfigRequest) => void;
};

const AlertConfig: React.FC<AlertConfigProps> = ({ channel, handleSave }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation('configuration');
  const [configurationVisible, setConfigurationVisible] = useState<boolean>(false);
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const [barrierEnabled, setBarrierEnabled] = useState<boolean>(false);
  const [misdirectionEnabled, setMisdirectionEnabled] = useState<boolean>(false);
  const [externalSourceEnabled, setExternalSourceEnabled] = useState<boolean>(false);
  const [time, setTime] = useState<string>(millisecondsToTime(0));
  const alertConfig = (channel.data as ChannelGateType).alertConfig;

  useEffect(() => {
    const configuration = new URLSearchParams(search).get('barrier-alert');
    setConfigurationVisible(!!configuration);
  }, [search]);

  useEffect(() => {
    const config = alertConfig;

    if (config) {
      setBarrierEnabled(config.barrierAlertEnabled);
      setMisdirectionEnabled(config.misdirectionAlertEnabled);
      setExternalSourceEnabled(config.externalSourceTriggeredAlertEnabled);
      setTime(millisecondsToTime(config?.openedIntervalToNotifyInMs || 0));
    }
  }, [alertConfig]);

  const openConfiguration = () => {
    navigate(pathnameWithParameters(pathname, search, { key: 'barrier-alert', value: true }));
  };

  const handleSubmit = () => {
    handleSave({
      barrierAlertEnabled: barrierEnabled,
      misdirectionAlertEnabled: misdirectionEnabled,
      externalSourceTriggeredAlertEnabled: externalSourceEnabled,
      openedIntervalToNotifyInMs: timeToMilliseconds(time),
    });
  };

  const btnDisabled = useMemo(() => {
    const original = omitDeep(cloneDeep(alertConfig), ['__typename']);

    const newData: GateChannelAlertConfigResponse = {
      barrierAlertEnabled: barrierEnabled,
      externalSourceTriggeredAlertEnabled: externalSourceEnabled,
      misdirectionAlertEnabled: misdirectionEnabled,
      openedIntervalToNotifyInMs: timeToMilliseconds(time),
    };

    return isEqual(original, newData);
  }, [alertConfig, barrierEnabled, misdirectionEnabled, externalSourceEnabled, time]);

  return (
    <>
      {!configurationVisible ? (
        <>
          <hr />
          <ArrowButton title={t('barrierAlert.label')} className="m-b-12" onClick={openConfiguration} />
        </>
      ) : (
        <Page
          kind="above"
          contentClassName="barrier-alert"
          header={
            <>
              <NavHeader />
              <Header title={t('barrierAlert.header')} />
            </>
          }
        >
          <div className="row_container m-b-24">
            <p>{t('barrierAlert.misdirectionAlertEnabled')}</p>
            <Switch checked={misdirectionEnabled} onChange={() => setMisdirectionEnabled(!misdirectionEnabled)} />
          </div>
          <div className="row_container m-b-24">
            <p>{t('barrierAlert.externalSourceTriggeredAlertEnabled')}</p>
            <Switch checked={externalSourceEnabled} onChange={() => setExternalSourceEnabled(!externalSourceEnabled)} />
          </div>
          <div className="row_container m-b-24">
            <p>{t('barrierAlert.barrierAlertEnabled')}</p>
            <Switch checked={barrierEnabled} onChange={() => setBarrierEnabled(!barrierEnabled)} />
          </div>
          <InputMui
            value={time}
            readOnly
            label={t('barrierAlert.alertTime')}
            onClickInput={() => setShowTimePicker(true)}
            endIcon={<IconEdit />}
            labelSx={{ fontSize: 16, fontWeight: 400, color: 'var(--color-type-primary)' }}
          />
          <DialogTimePicker
            header={`${t('barrierAlert.alertTime')}`}
            open={showTimePicker}
            setOpen={setShowTimePicker}
            onSave={(value) => setTime(value)}
            time={time}
            showSeconds
          />
          <SubmitButton disabled={btnDisabled} type="button" onClick={handleSubmit} />
        </Page>
      )}
    </>
  );
};

export default AlertConfig;
